import React, { useCallback, useState } from 'react';
import { DndContext, DragOverlay, closestCorners, KeyboardSensor, PointerSensor, useSensor, useSensors, DragStartEvent, DragEndEvent } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { FileTextIcon, TrendingUpIcon, ClockIcon, CheckCircle2Icon } from 'lucide-react';
import { useConditions } from '../hooks/useConditions';
import { StatusColumn } from '../components/StatusColumn';
import { FilterBar } from '../components/FilterBar';
import { AddConditionModal } from '../components/AddConditionModal';
import { ConditionCardOverlay } from '../components/ConditionCard';
import { Condition, ConditionStatus, STATUSES } from '../types/condition';
type ConditionBoardProps = {
  onViewModeChange?: (mode: 'table' | 'board') => void;
};
export function ConditionBoard({
  onViewModeChange
}: ConditionBoardProps) {
  const {
    conditions,
    conditionsByStatus,
    searchQuery,
    setSearchQuery,
    activeCategories,
    toggleCategory,
    activeAssignees,
    toggleAssignee,
    setAllAssignees,
    activeStatuses,
    toggleStatus,
    setAllStatuses,
    moveCondition,
    addCondition,
    updateCondition,
    totalCount
  } = useConditions();
  const [activeCondition, setActiveCondition] = useState<Condition | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalDefaultStatus, setModalDefaultStatus] = useState<ConditionStatus>('Pending');
  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8
    }
  }), useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates
  }));
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const {
      active
    } = event;
    const condition = conditions.find(c => c.id === active.id);
    if (condition) {
      setActiveCondition(condition);
    }
  }, [conditions]);
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const {
      active,
      over
    } = event;
    setActiveCondition(null);
    if (!over) return;
    const conditionId = active.id as string;
    const overId = over.id as string;
    // Check if dropped on a status column
    if (STATUSES.includes(overId as ConditionStatus)) {
      moveCondition(conditionId, overId as ConditionStatus);
      return;
    }
    // Check if dropped on another condition (get its status)
    const overCondition = conditions.find(c => c.id === overId);
    if (overCondition) {
      moveCondition(conditionId, overCondition.status);
    }
  }, [conditions, moveCondition]);
  const handleAddClick = useCallback((status?: ConditionStatus) => {
    setModalDefaultStatus(status || 'Pending');
    setIsModalOpen(true);
  }, []);
  const handleUpdateAssignee = useCallback((id: string, assignee: string) => {
    updateCondition(id, {
      assignee
    });
  }, [updateCondition]);
  // Calculate stats from all conditions (not filtered)
  const allConditionsByStatus = {
    Pending: conditions.filter(c => c.status === 'Pending').length,
    'In Progress': conditions.filter(c => c.status === 'In Progress').length,
    Approved: conditions.filter(c => c.status === 'Approved').length,
    Waived: conditions.filter(c => c.status === 'Waived').length
  };
  const completionRate = totalCount > 0 ? Math.round((allConditionsByStatus.Approved + allConditionsByStatus.Waived) / totalCount * 100) : 0;
  return <div className="min-h-screen bg-gray-100 flex flex-col" data-id="element-341">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4" data-id="element-342">
        <div className="flex flex-col gap-4" data-id="element-343">
          {/* Title Row */}
          <div className="flex items-center justify-between" data-id="element-344">
            <div data-id="element-345">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-346">
                Condition Tracker
              </h1>
              <p className="text-sm text-gray-500 mt-1" data-id="element-347">
                Loan #2024-001234 • Johnson, Michael & Sarah
              </p>
            </div>

            {/* View Toggle */}
            {onViewModeChange && <button onClick={() => onViewModeChange('table')} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-348">
                Switch to Table View
              </button>}
          </div>

          {/* Quick Stats */}
          <div className="flex items-center gap-6" data-id="element-349">
            <div className="flex items-center gap-2" data-id="element-350">
              <div className="p-2 bg-slate-100 rounded-lg" data-id="element-351">
                <FileTextIcon className="w-4 h-4 text-slate-600" data-id="element-352" />
              </div>
              <div data-id="element-353">
                <p className="text-xs text-gray-500" data-id="element-354">Pending</p>
                <p className="text-lg font-semibold text-gray-900" data-id="element-355">
                  {allConditionsByStatus.Pending}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2" data-id="element-356">
              <div className="p-2 bg-blue-100 rounded-lg" data-id="element-357">
                <ClockIcon className="w-4 h-4 text-blue-600" data-id="element-358" />
              </div>
              <div data-id="element-359">
                <p className="text-xs text-gray-500" data-id="element-360">In Progress</p>
                <p className="text-lg font-semibold text-gray-900" data-id="element-361">
                  {allConditionsByStatus['In Progress']}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2" data-id="element-362">
              <div className="p-2 bg-emerald-100 rounded-lg" data-id="element-363">
                <CheckCircle2Icon className="w-4 h-4 text-emerald-600" data-id="element-364" />
              </div>
              <div data-id="element-365">
                <p className="text-xs text-gray-500" data-id="element-366">Approved</p>
                <p className="text-lg font-semibold text-gray-900" data-id="element-367">
                  {allConditionsByStatus.Approved}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2" data-id="element-368">
              <div className="p-2 bg-purple-100 rounded-lg" data-id="element-369">
                <TrendingUpIcon className="w-4 h-4 text-purple-600" data-id="element-370" />
              </div>
              <div data-id="element-371">
                <p className="text-xs text-gray-500" data-id="element-372">Complete</p>
                <p className="text-lg font-semibold text-gray-900" data-id="element-373">
                  {completionRate}%
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Filter Bar */}
      <FilterBar searchQuery={searchQuery} onSearchChange={setSearchQuery} activeCategories={activeCategories} onToggleCategory={toggleCategory} activeAssignees={activeAssignees} onToggleAssignee={toggleAssignee} onSetAllAssignees={setAllAssignees} activeStatuses={activeStatuses} onToggleStatus={toggleStatus} onSetAllStatuses={setAllStatuses} onAddClick={() => handleAddClick()} totalCount={totalCount} filteredCount={conditions.length} data-id="element-374" />

      {/* Board */}
      <main className="flex-1 overflow-x-auto p-6" data-id="element-375">
        <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={handleDragStart} onDragEnd={handleDragEnd} data-id="element-376">
          <div className="flex gap-4 min-w-max" data-id="element-377">
            {STATUSES.map(status => <StatusColumn key={status} status={status} conditions={conditionsByStatus[status]} onAddClick={handleAddClick} onUpdateAssignee={handleUpdateAssignee} data-id="element-378" />)}
          </div>

          <DragOverlay data-id="element-379">
            {activeCondition ? <ConditionCardOverlay condition={activeCondition} data-id="element-380" /> : null}
          </DragOverlay>
        </DndContext>
      </main>

      {/* Add Condition Modal */}
      <AddConditionModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onAdd={addCondition} defaultStatus={modalDefaultStatus} data-id="element-381" />
    </div>;
}